import { Drawer, Empty, Spin } from 'antd'
import Custompreviewimg from '@/components/PreviewUser'
import { ProjectMaterialPoolDetailDTO } from '@/api/projectPoolApi'
import { FIELD_COLUMNS, EMPTY_TEXT, fieldValueOf } from '../PoolTable'
import { PlatformStatus, PoolImage } from '../PoolCards'
import Style from '../../style.module.scss'
import useDetailDrawer, { DetailDrawerProps } from './hook'

/** 抽屉宽度：桌面 min(860px, 视口-48px)，窄屏（≤760px）全宽（PRD LIST-001 3.5） */
const DRAWER_WIDTH = 'min(860px, calc(100vw - 48px))'
const NARROW_SCREEN = 760

/** 字段分区（PRD LIST-001 3.5）；厂控、实供只以图标形式出现在平台状态同行 */
const CORE_TEXT_FIELDS = ['xhTitle', 'customerTitle']
const CORE_FIELDS = ['xhCode', 'customerProductCode', 'projectPrice']
const BASE_FIELDS = [
  'brandName',
  'customerCategoryName',
  'unitName',
  'supplierName',
  'standardCode',
  'remark',
  'areaInventoryList',
  'multiplePurchaseQty',
  'minimumOrderQuantity',
  'materialCode',
]
const PRICE_FIELDS = ['controlPrice', 'maxPrice', 'minPrice', 'salesVolume']
/** 金额字段带 ¥ 前缀 */
const MONEY_FIELDS = ['projectPrice', 'controlPrice', 'maxPrice', 'minPrice']

/** 详情展示值：金额加 ¥，空值“-” */
const valueOf = (code: string, detail: ProjectMaterialPoolDetailDTO) => {
  const value = fieldValueOf(code, detail)
  return MONEY_FIELDS.includes(code) && value !== EMPTY_TEXT
    ? `¥${value}`
    : value
}

/**
 * 项目商品池 - 物料详情抽屉（按原型）：主图图库 + 核心信息、基础信息、价格与销售、详情图。
 * 只展示当前角色可见字段，已授权无值显示“-”（PRD DET-001 3.2）；图片可切换、点击放大预览（3.3）。
 */
const DetailDrawer = (props: DetailDrawerProps) => {
  const { xhCode, onClose, onAfterClose } = props
  const {
    detail,
    fields,
    error,
    loading,
    mainIndex,
    preview,
    changeMainImage,
    openPreview,
    closePreview,
  } = useDetailDrawer(props)
  const visible = (codes: string[]) =>
    codes.filter((code) => fields.includes(code))

  const renderSection = (title: string, codes: string[]) => {
    const visibleCodes = visible(codes)
    if (!detail || !visibleCodes.length) return null
    return (
      <div className={Style.detailSection}>
        <div className={Style.detailSectionTitle}>{title}</div>
        <div className={Style.detailGrid}>
          {visibleCodes.map((code) => (
            <div key={code} className={Style.detailItem}>
              <span className={Style.detailLabel}>
                {FIELD_COLUMNS[code].title}
              </span>
              <span className={Style.detailValue}>{valueOf(code, detail)}</span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const mainImages = detail?.mainImageUrls || []
  const detailImages = detail?.detailImageUrls || []

  return (
    <Drawer
      open={Boolean(xhCode)}
      title="物料详情"
      placement="right"
      width={window.innerWidth <= NARROW_SCREEN ? '100%' : DRAWER_WIDTH}
      className={Style.detailDrawer}
      destroyOnClose
      // 预览打开时 Esc 只关闭预览
      keyboard={!preview.data.length}
      onClose={onClose}
      afterOpenChange={(open) => {
        if (!open) onAfterClose()
      }}
    >
      <Spin spinning={loading}>
        {error && <Empty className={Style.listEmpty} description={error} />}
        {detail && (
          <>
            <div className={Style.detailCore}>
              <div className={Style.gallery}>
                <div
                  className={Style.galleryMain}
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    mainImages.length && openPreview(mainImages, mainIndex)
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && mainImages.length) {
                      openPreview(mainImages, mainIndex)
                    }
                  }}
                >
                  <PoolImage
                    key={mainImages[mainIndex]}
                    url={mainImages[mainIndex] || null}
                  />
                  {mainImages.length > 0 && (
                    <span className={Style.galleryCount}>
                      {mainIndex + 1} / {mainImages.length}
                    </span>
                  )}
                </div>
                <div className={Style.thumbnails}>
                  {mainImages.map((url, index) => (
                    <button
                      key={url}
                      type="button"
                      className={
                        index === mainIndex
                          ? Style.thumbnailActive
                          : Style.thumbnail
                      }
                      onClick={() => changeMainImage(index)}
                    >
                      <PoolImage url={url} />
                    </button>
                  ))}
                </div>
              </div>
              <div className={Style.coreFields}>
                {visible(CORE_TEXT_FIELDS).map((code) => (
                  <div key={code} className={Style.coreRow}>
                    <span className={Style.detailLabel}>
                      {FIELD_COLUMNS[code].title}
                    </span>
                    <span
                      className={Style.coreTitle}
                      title={String(valueOf(code, detail))}
                    >
                      {valueOf(code, detail)}
                    </span>
                  </div>
                ))}
                {visible(CORE_FIELDS).map((code) => (
                  <div key={code} className={Style.coreRow}>
                    <span className={Style.detailLabel}>
                      {FIELD_COLUMNS[code].title}
                    </span>
                    <span
                      className={
                        code === 'projectPrice'
                          ? Style.corePrice
                          : Style.detailValue
                      }
                    >
                      {valueOf(code, detail)}
                    </span>
                  </div>
                ))}
                {fields.includes('platformStatus') && (
                  <div className={Style.coreRow}>
                    <span className={Style.detailLabel}>平台状态</span>
                    <PlatformStatus
                      item={detail}
                      fields={fields}
                      withLabel={false}
                    />
                  </div>
                )}
              </div>
            </div>
            {renderSection('基础信息', BASE_FIELDS)}
            {renderSection('价格与销售', PRICE_FIELDS)}
            <div className={Style.detailSection}>
              <div className={Style.detailSectionTitle}>详情图</div>
              <div className={Style.detailImages}>
                {detailImages.length ? (
                  detailImages.map((url, index) => (
                    <button
                      key={url}
                      type="button"
                      className={Style.detailImage}
                      onClick={() => openPreview(detailImages, index)}
                    >
                      <PoolImage url={url} />
                    </button>
                  ))
                ) : (
                  <PoolImage url={null} />
                )}
              </div>
            </div>
          </>
        )}
      </Spin>
      <Custompreviewimg images={preview} onClose={closePreview} />
    </Drawer>
  )
}

export default DetailDrawer
import { Drawer, Empty, Spin } from 'antd'
import Custompreviewimg from '@/components/PreviewUser'
import { ProjectMaterialPoolDetailDTO } from '@/api/projectPoolApi'
import { FIELD_COLUMNS, EMPTY_TEXT, fieldValueOf } from '../PoolTable'
import { PlatformStatus, PoolImage } from '../PoolCards'
import Style from '../../style.module.scss'
import useDetailDrawer, { DetailDrawerProps } from './hook'

/** 抽屉宽度：桌面 min(860px, 视口-48px)，窄屏（≤760px）全宽（PRD LIST-001 3.5） */
const DRAWER_WIDTH = 'min(860px, calc(100vw - 48px))'
const NARROW_SCREEN = 760

/** 字段分区（PRD LIST-001 3.5）；厂控、实供只以图标形式出现在平台状态同行 */
const CORE_TEXT_FIELDS = ['xhTitle', 'customerTitle']
const CORE_FIELDS = ['xhCode', 'customerProductCode', 'projectPrice']
const BASE_FIELDS = [
  'brandName',
  'customerCategoryName',
  'unitName',
  'supplierName',
  'standardCode',
  'remark',
  'areaInventoryList',
  'multiplePurchaseQty',
  'minimumOrderQuantity',
  'materialCode',
]
const PRICE_FIELDS = ['controlPrice', 'maxPrice', 'minPrice', 'salesVolume']
/** 金额字段带 ¥ 前缀 */
const MONEY_FIELDS = ['projectPrice', 'controlPrice', 'maxPrice', 'minPrice']

/** 详情展示值：金额加 ¥，空值“-” */
const valueOf = (code: string, detail: ProjectMaterialPoolDetailDTO) => {
  const value = fieldValueOf(code, detail)
  return MONEY_FIELDS.includes(code) && value !== EMPTY_TEXT
    ? `¥${value}`
    : value
}

/**
 * 项目商品池 - 物料详情抽屉（按原型）：主图图库 + 核心信息、基础信息、价格与销售、详情图。
 * 只展示当前角色可见字段，已授权无值显示“-”（PRD DET-001 3.2）；图片可切换、点击放大预览（3.3）。
 */
const DetailDrawer = (props: DetailDrawerProps) => {
  const { xhCode, onClose, onAfterClose } = props
  const {
    detail,
    fields,
    error,
    loading,
    mainIndex,
    preview,
    changeMainImage,
    openPreview,
    closePreview,
  } = useDetailDrawer(props)
  const visible = (codes: string[]) =>
    codes.filter((code) => fields.includes(code))

  const renderSection = (title: string, codes: string[]) => {
    const visibleCodes = visible(codes)
    if (!detail || !visibleCodes.length) return null
    return (
      <div className={Style.detailSection}>
        <div className={Style.detailSectionTitle}>{title}</div>
        <div className={Style.detailGrid}>
          {visibleCodes.map((code) => (
            <div key={code} className={Style.detailItem}>
              <span className={Style.detailLabel}>
                {FIELD_COLUMNS[code].title}
              </span>
              <span className={Style.detailValue}>{valueOf(code, detail)}</span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const mainImages = detail?.mainImageUrls || []
  const detailImages = detail?.detailImageUrls || []

  return (
    <Drawer
      open={Boolean(xhCode)}
      title="物料详情"
      placement="right"
      width={window.innerWidth <= NARROW_SCREEN ? '100%' : DRAWER_WIDTH}
      className={Style.detailDrawer}
      destroyOnClose
      // 预览打开时 Esc 只关闭预览
      keyboard={!preview.data.length}
      onClose={onClose}
      afterOpenChange={(open) => {
        if (!open) onAfterClose()
      }}
    >
      <Spin spinning={loading}>
        {error && <Empty className={Style.listEmpty} description={error} />}
        {detail && (
          <>
            <div className={Style.detailCore}>
              <div className={Style.gallery}>
                <div
                  className={Style.galleryMain}
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    mainImages.length && openPreview(mainImages, mainIndex)
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && mainImages.length) {
                      openPreview(mainImages, mainIndex)
                    }
                  }}
                >
                  <PoolImage
                    key={mainImages[mainIndex]}
                    url={mainImages[mainIndex] || null}
                  />
                  {mainImages.length > 0 && (
                    <span className={Style.galleryCount}>
                      {mainIndex + 1} / {mainImages.length}
                    </span>
                  )}
                </div>
                <div className={Style.thumbnails}>
                  {mainImages.map((url, index) => (
                    <button
                      key={url}
                      type="button"
                      className={
                        index === mainIndex
                          ? Style.thumbnailActive
                          : Style.thumbnail
                      }
                      onClick={() => changeMainImage(index)}
                    >
                      <PoolImage url={url} />
                    </button>
                  ))}
                </div>
              </div>
              <div className={Style.coreFields}>
                {visible(CORE_TEXT_FIELDS).map((code) => (
                  <div key={code} className={Style.coreRow}>
                    <span className={Style.detailLabel}>
                      {FIELD_COLUMNS[code].title}
                    </span>
                    <span
                      className={Style.coreTitle}
                      title={String(valueOf(code, detail))}
                    >
                      {valueOf(code, detail)}
                    </span>
                  </div>
                ))}
                {visible(CORE_FIELDS).map((code) => (
                  <div key={code} className={Style.coreRow}>
                    <span className={Style.detailLabel}>
                      {FIELD_COLUMNS[code].title}
                    </span>
                    <span
                      className={
                        code === 'projectPrice'
                          ? Style.corePrice
                          : Style.detailValue
                      }
                    >
                      {valueOf(code, detail)}
                    </span>
                  </div>
                ))}
                {fields.includes('platformStatus') && (
                  <div className={Style.coreRow}>
                    <span className={Style.detailLabel}>平台状态</span>
                    <PlatformStatus
                      item={detail}
                      fields={fields}
                      withLabel={false}
                    />
                  </div>
                )}
              </div>
            </div>
            {renderSection('基础信息', BASE_FIELDS)}
            {renderSection('价格与销售', PRICE_FIELDS)}
            <div className={Style.detailSection}>
              <div className={Style.detailSectionTitle}>详情图</div>
              <div className={Style.detailImages}>
                {detailImages.length ? (
                  detailImages.map((url, index) => (
                    <button
                      key={url}
                      type="button"
                      className={Style.detailImage}
                      onClick={() => openPreview(detailImages, index)}
                    >
                      <PoolImage url={url} />
                    </button>
                  ))
                ) : (
                  <PoolImage url={null} />
                )}
              </div>
            </div>
          </>
        )}
      </Spin>
      <Custompreviewimg images={preview} onClose={closePreview} />
    </Drawer>
  )
}

export default DetailDrawer
